import type React from "react";

export const sectionIds = [
  "general",
  "team",
  "restrictions",
  "match",
  "matchmaking",
  "ranks",
  "danger",
] as const;

export type SectionId = (typeof sectionIds)[number];

export type NavItem = {
  id: SectionId;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  danger?: boolean;
};

export const sectionTitles: Record<SectionId, string> = {
  general: "General",
  team: "Team Config",
  restrictions: "Restrictions",
  match: "Match Settings",
  matchmaking: "Advanced Matchmaking",
  ranks: "Rank Config",
  danger: "Delete",
};

export const sectionDocs: Record<SectionId, React.ReactNode> = {
  general: (
    <div className="space-y-3 text-sm text-surface-300">
      <p>
        Configure the queue's display name, description, banner, and icon. These are shown to
        players browsing available queues.
      </p>
      <p>
        The <span className="text-white font-medium">Enabled</span> toggle controls whether players
        can join this queue. Disabling it won't affect matches already in progress.
      </p>
    </div>
  ),
  team: (
    <div className="space-y-3 text-sm text-surface-300">
      <p>
        <span className="text-white font-medium">Team Size</span> is the number of players per team.{" "}
        <span className="text-white font-medium">Team Count</span> is how many teams compete in each
        match.
      </p>
      <p>
        For a standard 5v5, set team size to 5 and team count to 2. For a 10-player FFA, set team
        size to 1 and team count to 10.
      </p>
      <p>
        <span className="text-white font-medium">Allow Full Teams</span> lets pre-made groups that
        fill an entire team slot queue together. Disabling this forces solo or partial-party queuing
        only.
      </p>
    </div>
  ),
  restrictions: (
    <div className="space-y-3 text-sm text-surface-300">
      <p>
        <span className="text-white font-medium">Max Party Size</span> caps how many players can
        queue together as a group. Set it to 1 for solo-only queues.
      </p>
      <p>
        <span className="text-white font-medium">Party ELO Disparity</span> limits the rating gap
        between the highest and lowest rated members of a party. This prevents high-rated players
        from boosting low-rated friends. Leave blank for no restriction.
      </p>
    </div>
  ),
  match: (
    <div className="space-y-3 text-sm text-surface-300">
      <p>
        <span className="text-white font-medium">Accept Timeout</span> — when a match is found, all
        players must accept within this window. If anyone declines or times out, the remaining
        players are re-queued.
      </p>
      <p>
        <span className="text-white font-medium">No-Show Timeout</span> — after accepting, players
        must join the game server within this time or they're marked absent and may receive a queue
        ban.
      </p>
      <h4 className="text-white font-medium pt-1">Host Selection</h4>
      <ul className="list-disc list-inside space-y-1 text-surface-400">
        <li>
          <span className="text-surface-300">Random</span> — a random player is chosen as host
        </li>
        <li>
          <span className="text-surface-300">Highest ELO</span> — the highest rated player hosts
        </li>
        <li>
          <span className="text-surface-300">Volunteer</span> — players can volunteer; random
          fallback
        </li>
      </ul>
      <h4 className="text-white font-medium pt-1">Team Formation</h4>
      <ul className="list-disc list-inside space-y-1 text-surface-400">
        <li>
          <span className="text-surface-300">Balanced</span> — algorithm distributes players for
          even teams
        </li>
        <li>
          <span className="text-surface-300">Captains</span> — top-rated players draft their teams
        </li>
        <li>
          <span className="text-surface-300">Random</span> — fully random team assignment
        </li>
      </ul>
    </div>
  ),
  matchmaking: (
    <div className="space-y-4 text-sm text-surface-300">
      <p>
        These are advanced parameters that control the matchmaking algorithm. Leave them at defaults
        unless you have a specific reason to change them.
      </p>
      <div>
        <h4 className="text-white font-medium mb-1">ELO Window</h4>
        <p>
          The matchmaker searches for opponents within a rating window. It starts at{" "}
          <span className="text-white">Base Range</span> (e.g. ±100 ELO) and expands by{" "}
          <span className="text-white">Expansion Step</span> every{" "}
          <span className="text-white">Expansion Interval</span> seconds until{" "}
          <span className="text-white">Max Disparity</span> is reached. Wider windows find matches
          faster but sacrifice quality.
        </p>
      </div>
      <div>
        <h4 className="text-white font-medium mb-1">Match Quality</h4>
        <p>
          Quality score = (ELO closeness × ELO Weight + wait time × Wait Weight) / Quality Divisor.
          Matches below <span className="text-white">Min Match Quality</span> are rejected. Higher
          ELO weight favors skill balance; higher wait weight reduces queue times.
        </p>
      </div>
      <div>
        <h4 className="text-white font-medium mb-1">Party Rating</h4>
        <p>
          A party's effective rating is a weighted blend: (highest member's rating × Highest Weight)
          + (party average × Average Weight). The two weights should sum to 1. Weighting toward the
          highest player penalizes boosting.
        </p>
      </div>
      <div>
        <h4 className="text-white font-medium mb-1">TrueSkill</h4>
        <p>
          <span className="text-white">Mu (μ)</span> is a player's estimated skill,{" "}
          <span className="text-white">Sigma (σ)</span> is uncertainty. New players start at Default
          Mu ± Default Sigma. <span className="text-white">Beta (β)</span> controls the performance
          variance — higher means more luck-dependent. <span className="text-white">Tau (τ)</span>{" "}
          adds uncertainty each game to allow ratings to drift over time.{" "}
          <span className="text-white">Display ELO Multiplier</span> converts internal TrueSkill
          values to a user-facing ELO number.
        </p>
      </div>
      <div>
        <h4 className="text-white font-medium mb-1">Bans</h4>
        <p>
          Players who dodge or no-show receive automatic queue bans. Ban duration = Base Duration ×
          Escalation Multiplier ^ (recent offenses - 1). Offenses are counted within the{" "}
          <span className="text-white">History Window</span>. The formula caps at{" "}
          <span className="text-white">Max Ban Duration</span>.
        </p>
      </div>
      <div>
        <h4 className="text-white font-medium mb-1">Reporting</h4>
        <p>
          After a match ends, players have this many seconds to submit results. If the timeout
          expires, the match may be auto-resolved or flagged for admin review.
        </p>
      </div>
      <div>
        <h4 className="text-white font-medium mb-1">Composition</h4>
        <p>
          Define required roles per team. Each slot specifies a role name and how many of that role
          are needed. For example, a 5v5 team might require 1 Tank, 1 Healer, and 3 DPS. The sum of
          all slot counts should equal the team size. When composition is set, players must select a
          role when queuing and the matchmaker will fill each role requirement before forming a
          match.
        </p>
      </div>
    </div>
  ),
  ranks: (
    <div className="space-y-3 text-sm text-surface-300">
      <p>
        Ranks are assigned based on ELO thresholds. A player gets the highest rank whose threshold
        they meet or exceed.
      </p>
      <p>For example, if Gold = 1200 and Platinum = 1500, a player with 1350 ELO is Gold.</p>
      <p>
        <span className="text-white font-medium">Prime</span> and{" "}
        <span className="text-white font-medium">Champion</span> are special override ranks awarded
        to the top N players by rating, regardless of their threshold-based rank. If Prime Top N =
        50, the top 50 players are Prime. Champion Top N works the same way but for a smaller elite
        group.
      </p>
    </div>
  ),
  danger: (
    <div className="space-y-3 text-sm text-surface-300">
      <p>
        Deleting a queue permanently removes all associated data including match history, player
        stats, ELO ratings, and rank assignments for this queue.
      </p>
      <p>This action cannot be undone. Players will lose all progress in this queue.</p>
    </div>
  ),
};
